import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import TradingPortfolioView from '../TradingPortfolioView';
import { useT } from './lib/siteTexts';

/* ── Demo cabinet (trading.com.ua/demo) ────────────────────────────────
 * A public example of the member-area portfolio page, pre-filled with a
 * $10 000 run over 04.05.2025–04.05.2026 (one year). The headline RETURN is
 * the active strategy's real result for that window (+73.5% — see analyzer
 * #7061920), pinned as a constant so the demo stays gentle and stable
 * regardless of live strategy swaps. Everything else — tickers, prices, the
 * trade log and the equity curve — is entirely invented so the strategy stays
 * hidden, and buy/sell reasons are not shown at all. */

const DEMO_START   = '2025-05-04';
const DEMO_END     = '2026-05-04';
const DEMO_AMOUNT  = 10000;
const YEARLY_PCT   = 73.5;                                   // real 1-year result of the active strategy
const FINAL_BALANCE = Math.round(DEMO_AMOUNT * (1 + YEARLY_PCT / 100)); // $17 350 over the 1-year window

/* Invented tickers — deliberately not real symbols. */
const DEMO_TICKERS = ['ARTX', 'BLMQ', 'CYRA', 'DLTN', 'EMBR', 'FLUX', 'GRYN', 'HLIX', 'IONQ2', 'JYRA'];

/* Deterministic PRNG so the demo looks identical on every visit. */
function lcg(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

const iso = (d) => d.toISOString().slice(0, 10) + ' 15:30';

/* ~32 invented trades across the one-year window; the last few BUYs stay open
 * so the "Активи портфеля" card shows open positions. Win-rate is set high on
 * purpose — the numbers are decorative, only the headline return is real. */
function buildDemoTrades() {
  const rnd = lcg(20250504);
  const start = new Date(`${DEMO_START}T00:00:00`);
  const trades = [];
  const N = 18;                       // buy/sell pairs
  const OPEN = 4;                     // last N buys left open
  for (let i = 0; i < N; i++) {
    const ticker   = DEMO_TICKERS[i % DEMO_TICKERS.length];
    const buyDate  = new Date(start);
    buyDate.setDate(buyDate.getDate() + Math.floor((330 / N) * i + rnd() * 10));
    const holdDays = 4 + Math.floor(rnd() * 28);
    const sellDate = new Date(buyDate);
    sellDate.setDate(sellDate.getDate() + holdDays);

    const buyPrice = +(15 + rnd() * 380).toFixed(2);
    const invest   = 480 + rnd() * 160;                    // ≈5% позиції від $10k
    const qty      = +(invest / buyPrice).toFixed(4);
    const win      = rnd() < 0.85;
    const gainPct  = win ? 1 + rnd() * 7 : -(1 + rnd() * 3.5);
    const sellPrice = +(buyPrice * (1 + gainPct / 100)).toFixed(2);
    const isOpen   = i >= N - OPEN;

    trades.push({
      time: iso(buyDate), ticker, action: 'BUY',
      qty, price: buyPrice, sum: +(qty * buyPrice).toFixed(2),
    });
    if (!isOpen) {
      trades.push({
        time: iso(sellDate), buy_time: iso(buyDate), ticker, action: 'SELL',
        qty, price: sellPrice, buy_price: buyPrice,
        sum: +(qty * sellPrice).toFixed(2),
        change_usd: +(qty * (sellPrice - buyPrice)).toFixed(2),
        pnl_pct: +gainPct.toFixed(2),
      });
    }
  }
  return trades.sort((a, b) => new Date(a.time) - new Date(b.time));
}

/* Invented equity curve: compounds smoothly from $10 000 to FINAL_BALANCE over
 * the window with a light deterministic wiggle. Its shape is decorative — it
 * does not trace the real strategy, so the strategy stays hidden. */
function buildCurve() {
  const rnd = lcg(45450);
  const pts = [];
  const start = new Date(`${DEMO_START}T00:00:00`);
  const days = 365;
  const growth = FINAL_BALANCE / DEMO_AMOUNT;
  for (let d = 0; d <= days; d += 2) {
    const t = d / days;
    const balance = DEMO_AMOUNT * Math.pow(growth, t) * (1 + (rnd() - 0.5) * 0.02);
    const date = new Date(start);
    date.setDate(date.getDate() + d);
    pts.push({ date: date.toISOString().slice(0, 10), balance: +balance.toFixed(2) });
  }
  pts.push({ date: DEMO_END, balance: FINAL_BALANCE });     // pin the exact endpoint
  return pts;
}

/* Smooth (wiggle-free) balance for the month-earnings cards, so "Зароблено за
 * місяць" is always a clean positive figure. */
function smoothBalance(dateStr) {
  const t = (new Date(dateStr) - new Date(`${DEMO_START}T00:00:00`)) / 86400000 / 365;
  const tt = Math.min(1, Math.max(0, t));
  return DEMO_AMOUNT * Math.pow(FINAL_BALANCE / DEMO_AMOUNT, tt);
}
function monthEarned(year, month /* 1-12 */) {
  const first = `${year}-${String(month).padStart(2, '0')}-01`;
  const nextFirst = month === 12
    ? `${year + 1}-01-01`
    : `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const last = new Date(new Date(nextFirst) - 86400000).toISOString().slice(0, 10);
  return +(smoothBalance(last) - smoothBalance(first)).toFixed(2);
}

function buildViewModel() {
  const equity = buildCurve();
  const trades = buildDemoTrades();

  const session = {
    id: 'demo',
    start_date: DEMO_START,
    result_json: {
      strategy_name:     'Активна стратегія',
      final_balance:     FINAL_BALANCE,
      yearly_return_pct: YEARLY_PCT,
      equity_curve:      equity,
      trades,
      chart_enabled:     false,
    },
  };
  const data = {
    id: 'demo',
    name: 'Демо-портфель',
    is_saved: false,
    params: {
      start_time: DEMO_START, stop_time: DEMO_END,
      data_type: 'hourly', initial_capital: DEMO_AMOUNT,
    },
    _meta: { engine: 'demo' },
    metrics: {
      starting_capital: DEMO_AMOUNT,
      final_balance:    FINAL_BALANCE,
      total_return:     YEARLY_PCT,              // one-year window → total == yearly
      yearly_return:    YEARLY_PCT,
      monthly_return:   YEARLY_PCT / 12,
      num_trades:       trades.length,
      win_rate:         85.7,
      sharpe_ratio:     null,
      max_drawdown:     null,
      start_time:       DEMO_START,
      stop_time:        DEMO_END,
    },
    trades,
    equity_curve:    equity,
    periods_results: [],
  };
  // "Зароблено за цей місяць" = квітень 2026, "за минулий місяць" = березень 2026.
  const monthlyPnl = { thisMonth: monthEarned(2026, 4), lastMonth: monthEarned(2026, 3) };
  return { session, data, monthlyPnl };
}

function DemoBanner() {
  const t = useT('demo.banner');
  return (
    <div style={{
      background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12,
      padding: '14px 20px', margin: '20px 0 4px',
      fontSize: 14, lineHeight: 1.55, color: '#0F171F',
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    }}>
      {t.rich('text', { linkStyle: { color: '#1C69E3', fontWeight: 600 } })}
    </div>
  );
}

export function DemoCabinet() {
  const model = useMemo(() => buildViewModel(), []);

  return (
    <>
      <Navbar onLight />
      <main style={{ background: '#F9FAFB', minHeight: '100vh', paddingTop: 96 }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', padding: '0 24px 48px' }}>
          <TradingPortfolioView
            session={model.session}
            data={model.data}
            banner={<DemoBanner />}
            monthlyPnlOverride={model.monthlyPnl}
            showYearly
          />
        </div>
      </main>
      <Footer />
    </>
  );
}
